import type { Icon } from "@phosphor-icons/react";
import {
	BellIcon,
	BroadcastIcon,
	BuildingsIcon,
	ChartPieIcon,
	ClipboardTextIcon,
	GaugeIcon,
	GearIcon,
	HouseIcon,
	KeyIcon,
	UsersThreeIcon,
	WifiHighIcon,
} from "@phosphor-icons/react";
import type { TFunction } from "i18next";
import { type Lang, ROUTE_SLUGS } from "@/config/routes";
import {
	hasDeploymentToolEnabled,
	type User,
	type UserRole,
} from "@/types/auth";

export type NavGroupKey =
	| "overview"
	| "measurements"
	| "infrastructure"
	| "installerTools";

export type NavItem = {
	title: string;
	url?: string;
	icon: Icon;
	roles: UserRole[];
	exact?: boolean;
	items?: NavItem[];
	visibleWhen?: (user: User) => boolean;
	group?: NavGroupKey;
};

const ROLE_GROUP_ORDER: NavGroupKey[] = [
	"overview",
	"measurements",
	"infrastructure",
	"installerTools",
];

export function getNavItems(t: TFunction, lang: Lang): NavItem[] {
	return [
		{
			title: t("nav.dashboard"),
			url: `/${lang}/`,
			icon: ChartPieIcon,
			roles: ["district_manager", "reseller_manager", "reseller"],
			exact: true,
			group: "overview",
		},
		{
			title: t("nav.myApartments"),
			url: `/${lang}/${ROUTE_SLUGS.myApartments[lang]}`,
			icon: HouseIcon,
			roles: ["end_user"],
			group: "overview",
		},
		{
			title: t("nav.meters"),
			icon: GaugeIcon,
			roles: ["district_manager"],
			group: "measurements",
			items: [
				{
					title: t("nav.waterMeters"),
					url: `/${lang}/${ROUTE_SLUGS.waterMeters[lang]}`,
					icon: GaugeIcon,
					roles: ["district_manager"],
				},
				{
					title: t("nav.heatMeters"),
					url: `/${lang}/${ROUTE_SLUGS.heatMeters[lang]}`,
					icon: GaugeIcon,
					roles: ["district_manager"],
				},
				{
					title: t("nav.electricityMeters"),
					url: `/${lang}/${ROUTE_SLUGS.electricityMeters[lang]}`,
					icon: GaugeIcon,
					roles: ["district_manager"],
				},
			],
		},
		{
			title: t("nav.readings"),
			url: `/${lang}/${ROUTE_SLUGS.readings[lang]}`,
			icon: ClipboardTextIcon,
			roles: ["reseller", "reseller_manager", "district_manager"],
			group: "measurements",
		},
		{
			title: t("nav.alerts"),
			url: `/${lang}/${ROUTE_SLUGS.alerts[lang]}`,
			icon: BellIcon,
			roles: ["district_manager"],
			group: "overview",
		},
		{
			title: t("nav.districts"),
			url: `/${lang}/${ROUTE_SLUGS.districts[lang]}`,
			icon: BuildingsIcon,
			roles: ["district_manager", "reseller"],
			group: "infrastructure",
		},
		{
			title: t("nav.apartments"),
			url: `/${lang}/${ROUTE_SLUGS.apartments[lang]}`,
			icon: HouseIcon,
			roles: ["district_manager"],
			group: "infrastructure",
		},
		{
			title: t("nav.managers"),
			url: `/${lang}/${ROUTE_SLUGS.managers[lang]}`,
			icon: UsersThreeIcon,
			roles: ["reseller"],
			group: "infrastructure",
		},
		{
			title: t("nav.sources"),
			url: `/${lang}/${ROUTE_SLUGS.sources[lang]}`,
			icon: WifiHighIcon,
			roles: ["reseller_manager", "reseller", "district_manager"],
			group: "infrastructure",
		},
		{
			title: t("nav.resellers"),
			url: `/${lang}/${ROUTE_SLUGS.resellers[lang]}`,
			icon: UsersThreeIcon,
			roles: ["reseller_manager"],
			group: "infrastructure",
		},
		{
			title: t("nav.keys"),
			url: `/${lang}/${ROUTE_SLUGS.keys[lang]}`,
			icon: KeyIcon,
			roles: ["district_manager"],
			group: "infrastructure",
		},
		{
			title: t("nav.mediaTransmitters"),
			url: `/${lang}/${ROUTE_SLUGS.mediaTransmitters[lang]}`,
			icon: BroadcastIcon,
			roles: [],
			visibleWhen: hasDeploymentToolEnabled,
			group: "installerTools",
		},
		{
			title: t("nav.deploymentReport"),
			url: `/${lang}/${ROUTE_SLUGS.deploymentReport[lang]}`,
			icon: ClipboardTextIcon,
			roles: [],
			visibleWhen: hasDeploymentToolEnabled,
			group: "installerTools",
		},
	];
}

export function getSettingsNavItem(t: TFunction, lang: Lang): NavItem {
	return {
		title: t("nav.settings"),
		url: `/${lang}/${ROUTE_SLUGS.settings[lang]}`,
		icon: GearIcon,
		roles: ["district_manager", "reseller_manager", "reseller", "end_user"],
	};
}

export type NavGroup = {
	label: string;
	role: "district_manager" | "reseller_manager" | "reseller" | null;
	items: NavItem[];
};

const ADMIN_GROUPED_ROLES = [
	"reseller_manager",
	"reseller",
	"district_manager",
] as const;

export function getNavGroupsForAdmin(
	t: TFunction,
	lang: Lang,
	user: User | null,
): NavGroup[] {
	const allItems = getNavItems(t, lang);
	const dashboard = allItems.find((item) => item.exact);

	const groups: NavGroup[] = [];

	if (dashboard) {
		groups.push({ label: "", role: null, items: [dashboard] });
	}

	const availableRoles = ADMIN_GROUPED_ROLES.filter((role) => {
		if (!user) return true;
		if (role === "district_manager") return user.district_manager_id != null;
		if (role === "reseller_manager") return user.reseller_manager_id != null;
		if (role === "reseller") return user.reseller_id != null;
		return false;
	});

	for (const role of availableRoles) {
		const roleLabel =
			role === "district_manager"
				? t("roles.districtManager")
				: role === "reseller_manager"
					? t("roles.resellerManager")
					: t("roles.reseller");

		const items = allItems.filter(
			(item) => !item.exact && item.roles.includes(role),
		);

		if (items.length > 0) {
			groups.push({ label: roleLabel, role, items });
		}
	}

	const flagOnly = allItems.filter(
		(item) => item.roles.length === 0 && item.visibleWhen,
	);
	const visibleFlagOnly = user
		? flagOnly.filter((item) => item.visibleWhen?.(user))
		: [];
	if (visibleFlagOnly.length > 0) {
		groups.push({
			label: t("nav.installerTools"),
			role: null,
			items: visibleFlagOnly,
		});
	}

	return groups;
}

export function getNavItemsForRole(
	t: TFunction,
	lang: Lang,
	role: UserRole,
	user: User | null,
): NavItem[] {
	const all = getNavItems(t, lang);
	if (role === "admin") return all;
	return all.filter((item) => {
		const roleOk = item.roles.includes(role);
		const flagOk = user != null && item.visibleWhen?.(user) === true;
		if (item.roles.length === 0) return flagOk;
		return roleOk || flagOk;
	});
}

function getGroupLabel(t: TFunction, key: NavGroupKey): string {
	switch (key) {
		case "overview":
			return t("nav.groups.overview");
		case "measurements":
			return t("nav.groups.measurements");
		case "infrastructure":
			return t("nav.groups.infrastructure");
		case "installerTools":
			return t("nav.installerTools");
	}
}

export function getNavGroupsForRole(
	t: TFunction,
	lang: Lang,
	role: UserRole,
	user: User | null,
): NavGroup[] {
	const items = getNavItemsForRole(t, lang, role, user);
	const groups: NavGroup[] = [];

	for (const key of ROLE_GROUP_ORDER) {
		const groupItems = items.filter((item) => item.group === key);
		if (groupItems.length === 0) continue;
		groups.push({
			label: getGroupLabel(t, key),
			role: null,
			items: groupItems,
		});
	}

	return groups;
}
